import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, Clock, User, CheckCircle, ChevronRight, ChevronLeft, Activity } from 'lucide-react';
import { format, addDays, startOfToday } from 'date-fns';
import { Button } from '../components/ui/Button';
import { useScreenInit } from '../useScreenInit.js';
const SERVICES = ['General Medical Consultations', 'Minor Surgical Procedures', 'Safe Medical Circumcision', 'Paternity Testing (DNA)', 'Immunizations', 'Pre-Employment Medical Examinations', 'Family Planning', 'Counseling Services', 'Chronic Disease Management', 'Physiotherapy', 'Optician Services', 'Antenatal Care', 'Postnatal Care', 'Laboratory Services', 'ECG Services', 'Ultrasound Services', 'Dentistry', 'Audiometry', 'Spirometry'];
const TIME_SLOTS = ['08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM'];
export function Booking() {
  const screenInit = useScreenInit();
  const [step, setStep] = useState(screenInit.step ?? 1);
  const [formData, setFormData] = useState({
    service: '',
    date: '',
    time: '',
    name: '',
    phone: '',
    email: '',
    notes: ''
  });
  const today = startOfToday();
  const next14Days = Array.from({
    length: 14
  }).map((_, i) => addDays(today, i + 1));
  const handleNext = () => setStep(s => Math.min(s + 1, 5));
  const handleBack = () => setStep(s => Math.max(s - 1, 1));
  const updateForm = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const isStepValid = () => {
    switch (step) {
      case 1:
        return !!formData.service;
      case 2:
        return !!formData.date;
      case 3:
        return !!formData.time;
      case 4:
        return !!formData.name && !!formData.phone;
      default:
        return true;
    }
  };
  const STEPS = [{
    num: 1,
    title: 'Service',
    icon: <Activity className="w-5 h-5" data-id="element-563" data-mp="380ntjyfk5050" />
  }, {
    num: 2,
    title: 'Date',
    icon: <CalendarIcon className="w-5 h-5" data-id="element-564" data-mp="3cxw2r9iq9tu4" />
  }, {
    num: 3,
    title: 'Time',
    icon: <Clock className="w-5 h-5" data-id="element-565" data-mp="2z7104eyto8yz" />
  }, {
    num: 4,
    title: 'Details',
    icon: <User className="w-5 h-5" data-id="element-566" data-mp="338cjw3v0lzz9" />
  }];
  return <div className="min-h-[calc(100vh-88px)] bg-brand-light py-12 px-4 sm:px-6 lg:px-8" data-id="element-567" data-mp="2p9mjofcwfk6o">
      <div className="max-w-3xl mx-auto" data-id="element-568" data-mp="2pbzcjnk73eyi">
        {/* Header */}
        <div className="text-center mb-10" data-id="element-569" data-mp="2uaalzx6kvng7">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-brand-navy mb-4" data-id="element-570" data-mp="2g8y7zraz9vwy">
            Book an Appointment
          </h1>
          <p className="text-slate-600" data-id="element-571" data-mp="2kbl1dwnarow9">
            Schedule your visit to Maisha Medical Center in a few simple steps.
          </p>
        </div>

        {/* Progress Bar */}
        {step < 5 && <div className="mb-10" data-id="element-572" data-mp="ihvjl8ufhwu0">
            <div className="flex items-center justify-between relative" data-id="element-573" data-mp="ih2j2pm2c3kb">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-200 rounded-full -z-10" data-id="element-574" data-mp="231qwiouez1si" />
              <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-brand-red rounded-full -z-10 transition-all duration-500" style={{
            width: `${(step - 1) / 3 * 100}%`
          }} data-id="element-575" data-mp="1z1gol6n2a1b0" />

              {STEPS.map(s => {
            const isActive = step === s.num;
            const isPast = step > s.num;
            return <div key={s.num} className="flex flex-col items-center gap-2 bg-brand-light px-2" data-id="element-576" data-mp="2vpwh9blgwqsc">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${isActive ? 'border-brand-red bg-brand-red text-white shadow-md' : isPast ? 'border-brand-red bg-white text-brand-red' : 'border-slate-300 bg-white text-slate-400'}`} data-id="element-577" data-mp="2qrbfv8uhc12b">
                      {isPast ? <CheckCircle className="w-5 h-5" data-id="element-578" data-mp="12t10yfovsifo" /> : s.icon}
                    </div>
                    <span className={`text-xs font-medium hidden sm:block ${isActive || isPast ? 'text-brand-navy' : 'text-slate-400'}`} data-id="element-579" data-mp="17s441ulawel0">
                      {s.title}
                    </span>
                  </div>;
          })}
            </div>
          </div>}

        {/* Form Container */}
        <div className="bg-white rounded-2xl shadow-soft-lg border border-slate-100 overflow-hidden" data-id="element-580" data-mp="k2t0648nm42n">
          <div className="p-6 md:p-10" data-id="element-581" data-mp="p0bcm4lvr1fe">
            <AnimatePresence mode="wait" data-id="element-582" data-mp="ts2ulqqyzytx">
              {/* STEP 1: SERVICE */}
              {step === 1 && <motion.div key="step1" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-583" data-mp="3sj4mo7fg984t">
                  <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-584" data-mp="3fxw8r0zgw6h8">
                    Select a Service
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-585" data-mp="36zkkh5m2pkry">
                    {SERVICES.map(service => <button key={service} onClick={() => updateForm('service', service)} className={`p-4 rounded-xl border-2 text-left transition-all ${formData.service === service ? 'border-brand-red bg-red-50 text-brand-red' : 'border-slate-100 hover:border-brand-navy/30 text-slate-700 hover:bg-slate-50'}`} data-id="element-586" data-mp="327kdit7l5rlv">
                        <span className="font-medium" data-id="element-587" data-mp="2ja00cje6ohh3">{service}</span>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 2: DATE */}
              {step === 2 && <motion.div key="step2" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-588" data-mp="1zny6zcun7wym">
                  <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-589" data-mp="2im0zw2qbae1p">
                    Select a Date
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-id="element-590" data-mp="3bb7y10lut1mf">
                    {next14Days.map(date => {
                  const dateStr = date.toISOString();
                  const isSunday = date.getDay() === 0;
                  return <button key={dateStr} disabled={isSunday} onClick={() => updateForm('date', dateStr)} className={`p-4 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${isSunday ? 'opacity-50 cursor-not-allowed bg-slate-50 border-slate-100' : formData.date === dateStr ? 'border-brand-red bg-red-50 text-brand-red' : 'border-slate-100 hover:border-brand-navy/30 text-slate-700 hover:bg-slate-50'}`} data-id="element-591" data-mp="3pedml99ffc3j">
                          <span className="text-sm font-medium uppercase tracking-wider" data-id="element-592" data-mp="3gguvbn56w1j7">
                            {format(date, 'EEE')}
                          </span>
                          <span className="text-2xl font-heading font-bold" data-id="element-593" data-mp="vpb5anj0o1zm">
                            {format(date, 'd')}
                          </span>
                          <span className="text-xs" data-id="element-594" data-mp="qsuc3ewymxfo">{format(date, 'MMM')}</span>
                        </button>;
                })}
                  </div>
                </motion.div>}

              {/* STEP 3: TIME */}
              {step === 3 && <motion.div key="step3" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-595" data-mp="2u5jripddu21i">
                  <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-596" data-mp="3hxqvoapcndbh">
                    Select a Time
                  </h2>
                  <p className="text-slate-600 mb-4" data-id="element-597" data-mp="1ilo5khm7zkou">
                    Showing available slots for{' '}
                    <strong className="text-brand-navy" data-id="element-598" data-mp="1njg59xt3wb3l">
                      {format(new Date(formData.date), 'EEEE, MMMM d, yyyy')}
                    </strong>
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-599" data-mp="1b7dsbk0vba41">
                    {TIME_SLOTS.map(time => <button key={time} onClick={() => updateForm('time', time)} className={`p-4 rounded-xl border-2 text-center transition-all ${formData.time === time ? 'border-brand-red bg-red-50 text-brand-red' : 'border-slate-100 hover:border-brand-navy/30 text-slate-700 hover:bg-slate-50'}`} data-id="element-600" data-mp="167soav6ucbkg">
                        <span className="font-medium" data-id="element-601" data-mp="1p2g4gowb5vpa">{time}</span>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 4: DETAILS */}
              {step === 4 && <motion.div key="step4" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-602" data-mp="9ijpnrhav0m1">
                  <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-603" data-mp="2gtz8a7hyavn3">
                    Patient Details
                  </h2>

                  <div className="bg-brand-lighter p-4 rounded-xl mb-6 flex flex-col sm:flex-row gap-4 justify-between border border-slate-100" data-id="element-604" data-mp="2qmofuradrpd5">
                    <div data-id="element-605" data-mp="34jk2jmjk1fbo">
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-606" data-mp="2zmtby5a4j1c0">
                        Service
                      </div>
                      <div className="font-medium text-brand-navy" data-id="element-607" data-mp="1a85av06zh5ta">
                        {formData.service}
                      </div>
                    </div>
                    <div data-id="element-608" data-mp="3gvm5r8lzr1y1">
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-609" data-mp="3gx73t87il30n">
                        Date & Time
                      </div>
                      <div className="font-medium text-brand-navy" data-id="element-610" data-mp="383tx93gj22w3">
                        {format(new Date(formData.date), 'MMM d, yyyy')} at{' '}
                        {formData.time}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4" data-id="element-611" data-mp="2y7h72fk6y763">
                    <div data-id="element-612" data-mp="334r5ultgzv6l">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-613" data-mp="335aflf8yvd9j">
                        Full Name *
                      </label>
                      <input type="text" value={formData.name} onChange={e => updateForm('name', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all" placeholder="John Doe" data-id="element-614" data-mp="2t61k2hygdql2" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-615" data-mp="1vewlj0o54czl">
                      <div data-id="element-616" data-mp="1rc00l3b4o4sh">
                        <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-617" data-mp="1rdb36zsu76i0">
                          Phone Number *
                        </label>
                        <input type="tel" value={formData.phone} onChange={e => updateForm('phone', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all" placeholder="+267 XXX XXXX" data-id="element-618" data-mp="2j132wdjgf52h" />
                      </div>
                      <div data-id="element-619" data-mp="2xylzu36acol0">
                        <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-620" data-mp="2xyc8ab5le9d7">
                          Email Address
                        </label>
                        <input type="email" value={formData.email} onChange={e => updateForm('email', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all" placeholder="john@example.com" data-id="element-621" data-mp="3a939o2y58yb3" />
                      </div>
                    </div>
                    <div data-id="element-622" data-mp="1ve3ansb1i2r">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-623" data-mp="1w60u45gppvo">
                        Additional Notes (Optional)
                      </label>
                      <textarea value={formData.notes} onChange={e => updateForm('notes', e.target.value)} rows={3} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all resize-none" placeholder="Any specific concerns or symptoms?" data-id="element-624" data-mp="aw2ihltfpoh8" />
                    </div>
                  </div>
                </motion.div>}

              {/* STEP 5: CONFIRMATION */}
              {step === 5 && <motion.div key="step5" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="text-center py-10" data-id="element-625" data-mp="3bc20xhnfrp89">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-626" data-mp="14kawjyxxnfps">
                    <CheckCircle className="w-10 h-10 text-green-600" data-id="element-627" data-mp="zm9ixtja08lp" />
                  </div>
                  <h2 className="text-3xl font-heading font-bold text-brand-navy mb-4" data-id="element-628" data-mp="2hita6vaxv9pv">
                    Request Received!
                  </h2>
                  <p className="text-slate-600 mb-8 max-w-md mx-auto" data-id="element-629" data-mp="28jzhvh4n55i4">
                    Thank you, {formData.name}. We have received your
                    appointment request for{' '}
                    <strong className="text-brand-navy" data-id="element-630" data-mp="318dje6uxujyf">
                      {formData.service}
                    </strong>{' '}
                    on{' '}
                    <strong className="text-brand-navy" data-id="element-631" data-mp="358xd10gaz5ft">
                      {format(new Date(formData.date), 'MMM d, yyyy')} at{' '}
                      {formData.time}
                    </strong>
                    . Our reception team will contact you shortly to confirm.
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-632" data-mp="3k5od7bxswl6h">
                    <Button href="/" variant="outline" data-id="element-633" data-mp="3oz95qogmkmya">
                      Return Home
                    </Button>
                    <Button href={`https://wa.me/26773241613?text=${encodeURIComponent(`Hello, I just submitted an appointment request online.\nName: ${formData.name}\nService: ${formData.service}\nDate: ${format(new Date(formData.date), 'MMM d, yyyy')}\nTime: ${formData.time}`)}`} variant="primary" className="bg-[#25D366] hover:bg-[#20bd5a] focus:ring-[#25D366]" data-id="element-634" data-mp="2m7rdwxrw69g">
                      Follow up on WhatsApp
                    </Button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Footer Actions */}
          {step < 5 && <div className="bg-slate-50 p-6 border-t border-slate-100 flex items-center justify-between" data-id="element-635" data-mp="313tj56rkpvdy">
              {step > 1 ? <button onClick={handleBack} className="flex items-center gap-2 text-slate-600 hover:text-brand-navy font-medium transition-colors" data-id="element-636" data-mp="2s4pyhycrd7xc">
                  <ChevronLeft className="w-4 h-4" data-id="element-637" data-mp="1bo9cw565w5rl" /> Back
                </button> : <div data-id="element-638" data-mp="1gndp74odujm5" />}

              <Button onClick={handleNext} disabled={!isStepValid()} variant="primary" className="gap-2" data-id="element-639" data-mp="1goexx2hxeu0p">
                {step === 4 ? 'Confirm Booking' : 'Continue'}{' '}
                <ChevronRight className="w-4 h-4" data-id="element-640" data-mp="sxro2bog9ubi" />
              </Button>
            </div>}
        </div>
      </div>
    </div>;
}